<template>
    <div class="travel-sim-checkout">
        <div v-if="currentStep == 1" class="container-1">
            <h1 class="sim-checkout-title hyper2 bold">{{ $t('web.travel-sim-checkout.checkout-title-1') }}</h1>
            <hr>
            <div class="notification is-danger" v-if="errorMessage">
                <button @click.prevent="errorMessage = null" class="delete"></button>
                {{ errorMessage }}
            </div>
            <div class="columns">
                <div class="column is-two-thirds">
                    <div class="sim-detail field-container">
                        <div class="title hyper5 bold black">{{ $t('web.travel-sim-checkout.simproduct-detail') }}</div>
                        <div class="border">
                            <div class="simcard-product">{{ $t('web.travel-sim-checkout.simproduct-title') }}</div>
                        </div>
                        <div class="sim-container border">
                            <div class="word-container">
                                <div class="word-container-1">
                                    <div class="hyper6 bold">{{ sim.name }}</div>
                                    <div><span class="bold" style="color:#0176EE">{{ currency_prefix }}{{ foreign_price_app.toLocaleString() }}</span> </div>
                                </div>
                                <div class="word-container-2">
                                    <div>{{ $t('web.travel-sim-checkout.simproduct-quantity') }}</div>
                                    <div class="control">
                                        <div class="select">
                                            <select v-model="form.quantity">
                                                <option :value="i" v-for="i in sim.inventory < 100 ? sim.inventory : 100">{{ i }}</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <img :src="sim.image_url" class="image-container">
                        </div>
                        <div class="check-out-price border">
                            <div class="sim-product bold">{{ $t('web.travel-sim-checkout.payment.price') }}</div>
                            <div class="hyper6 brand-blue bold">{{currency_prefix}}{{ (foreign_price_app * form.quantity).toLocaleString()}}</div>
                        </div>
                    </div>
                    <div v-if="user == null" @click.prevent="loginStep" class="simcheckout-login field-container">
                        <a class="login-column brand-blue">
                            <div class="login hyper6 bold">{{ $t('web.travel-sim-checkout.login') }}</div>
                            <span v-if="loginStep1 == 1" class="icon is-small"><i class="fas fa-angle-down"></i></span>
                            <span v-if="loginStep1 == 2" class="icon is-small"><i class="fas fa-angle-up"></i></span>
                        </a>
                    </div>

                    <transition name="fade">
                        <div v-if="loginStep1 == 2" class="hidden-part field-container">
                            <div class="columns">
                                <div class="text-input column">
                                    <form :action="routes.login" method="post">
                                        <input type="hidden" :value="token" name="_token" />
                                        <input type="hidden" value="user" name="type" />
                                        <div class="field">
                                            <label class="label">{{ $t('web.travel-sim-checkout.login-email') }}</label>
                                            <div class="control">
                                                <input name="email" class="input" type="text" :placeholder="$t('web.travel-sim-checkout.login-email')" required>
                                            </div>
                                        </div>
                                        <div class="field">
                                            <label class="label">{{ $t('web.travel-sim-checkout.login-password') }}</label>
                                            <div class="control">
                                                <input name="password" class="input" type="password" placeholder="******" required>
                                            </div>
                                        </div>
                                        <button type="submit" class="hyper6 bold white">{{ $t('web.travel-sim-checkout.login-button') }}</button>
                                    </form>
                                </div>
                                <div class="is-divider-vertical" :data-content="$t('web.travel-sim-checkout.vertical-divider')"></div>
                                <div class="image-container column">
                                    <a :href="routes.login_facebook" class="facebook-login hyper8 bold white"><img class="facebook" src="/images/simcheckout-facebook.svg">{{ $t('web.travel-sim-checkout.facebook-login') }}</a>
                                    <a :href="routes.login_google" class="google-login hyper8 bold black"><img class="google" src="/images/simcheckout-google.svg">{{ $t('web.travel-sim-checkout.google-login') }}</a>
                                </div>
                            </div>
                        </div>
                    </transition>

                    <div v-if="user != null" class="logged-in field-container">
                        <div class="gray500">{{ $t('web.travel-sim-checkout.logged-in') }}</div>
                        <div class="hyper6 black bold">{{ user.firstname }} {{ user.lastname }}</div>
                    </div>

                    <div class="user-information field-container">
                        <div class='title hyper5 bold black'>{{ $t('web.travel-sim-checkout.user-information') }}</div>
                        <div class="field">
                            <label class="label">{{ $t('web.travel-sim-checkout.user-firstname') }}*</label>
                            <div class="control">
                                <input v-model="form.firstname" class="input" type="text" :placeholder="$t('web.travel-sim-checkout.user-firstname')">
                            </div>
                        </div>

                        <div class="field">
                            <label class="label">{{ $t('web.travel-sim-checkout.user-lastname') }}*</label>
                            <div class="control">
                                <input @blur="autoFillName" v-model="form.lastname" class="input" type="text" :placeholder="$t('web.travel-sim-checkout.user-lastname')">
                            </div>
                        </div>

                        <div class="field">
                            <label class="label">{{ $t('web.travel-sim-checkout.user-email') }}*</label>
                            <div class="control">
                                <input v-model="form.email" class="input" type="text" :placeholder="$t('web.travel-sim-checkout.user-email')">
                            </div>
                        </div>

                        <div class="field is-horizontal">
                            <div class="field country-code">
                                <label class="label">{{ $t('web.travel-sim-checkout.country-code') }}*</label>
                                <div class="control">
                                    <div class="select">
                                        <select v-model="form.telAreaCode" @change="autoFillTelAreaCode">
                                            <option :value="phonecode" v-for="phonecode in sortedPhoneCodes">+{{ phonecode }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="field">
                                <label class="label">{{ $t('web.travel-sim-checkout.user-phone-number') }}*</label>
                                <p class="control">
                                    <input @blur="autoFillTel" v-model="form.tel" class="input" type="text" :placeholder="$t('web.travel-sim-checkout.user-phone-number')">
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="recipient-information field-container" v-if="sim.type == 'sim'">
                        <div class='title hyper5 bold black'>{{ $t('web.travel-sim-checkout.recipient-information') }}</div>
                        <div class="field">
                            <label class="label">{{ $t('web.travel-sim-checkout.recipient-name') }}*</label>
                            <div class="control">
                                <input v-model="form.recipientName" class="input" type="text" :placeholder="$t('web.travel-sim-checkout.recipient-name')">
                            </div>
                        </div>

                        <div class="field is-horizontal">
                            <div class="field country-code">
                                <label class="label">{{ $t('web.travel-sim-checkout.country-code') }}*</label>
                                <div class="control">
                                    <div class="select">
                                        <select v-model="form.recipientTelAreaCode">
                                            <option :value="phonecode" v-for="phonecode in sortedPhoneCodes">+{{ phonecode }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="field recipient-tel">
                                <label class="label">{{ $t('web.travel-sim-checkout.recipient-phone-number') }}*</label>
                                <p class="control">
                                    <input v-model="form.recipientTel" class="input" type="text" :placeholder="$t('web.travel-sim-checkout.recipient-phone-number')">
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="address field-container" v-if="sim.type == 'sim'">
                        <div class='title hyper5 bold black'>{{ $t('web.travel-sim-checkout.recipient-address') }}</div>
                        <div class="field">
                            <label class="label">{{ $t('web.travel-sim-checkout.address') }}</label>
                            <div class="control">
                                <input v-model="form.address1" class="input" type="text" :placeholder="$t('web.travel-sim-checkout.address-column1')">
                                <input v-model="form.address2" class="input" type="text" :placeholder="$t('web.travel-sim-checkout.address-column2')">
                                <input v-model="form.address3" class="input" type="text" :placeholder="$t('web.travel-sim-checkout.address-column3')">
                            </div>
                        </div>
                    </div>

                    <div class="delivery-method field-container" v-if="sim.type == 'sim'">
                        <div class='title hyper5 bold black'>{{ $t('web.travel-sim-checkout.delivery-method') }}</div>
                        <label class="radio bold">
                            <input type="radio" v-model="form.shippingMethod" value="hkpost" name="question">
                            {{ $t('web.travel-sim-checkout.delivery-method-1') }}
                        </label>
                        <p class="hk-post" v-html="$t('web.travel-sim-checkout.delivery-method-1-description')" />
                        <label class="radio bold">
                            <input type="radio" v-model="form.shippingMethod" value="sf" name="question">
                            {{ $t('web.travel-sim-checkout.delivery-method-2') }}
                        </label>
                        <p class="sf" v-html="$t('web.travel-sim-checkout.delivery-method-2-description')" />
<!--                        <label class="radio bold">-->
<!--                            <input type="radio" v-model="form.shippingMethod" value="international" name="question">-->
<!--                            {{ $t('web.travel-sim-checkout.delivery-method-3') }}-->
<!--                        </label>-->
<!--                        <p class="international-shipping">{{ $t('web.travel-sim-checkout.delivery-method-3-description') }}</p>-->
                        <article class="message">
                            <div class="message-header">
                                <div>{{ $t('web.travel-sim-checkout.delivery-message-header') }}</div>
                                <span class="icon has-text-info">
                                    <i class="fas fa-lg fa-info-circle"></i>
                                </span>
                            </div>
                            <div class="message-body" v-html="$t('web.travel-sim-checkout.delivery-message-body')" />
                        </article>
                    </div>

                    <div class="confirmation">
                        <button @click.prevent="confirmStep1" class="confirm-button hyper6 bold white">{{ $t('web.travel-sim-checkout.confirm-button') }}</button>
                        <button @click.prevent="goBack" class="back-button hyper6 bold black">{{ $t('web.travel-sim-checkout.back-button') }}</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 2 -->
        <div v-else class="container-2">
            <h1 class="sim-checkout-title hyper2 bold">{{ $t('web.travel-sim-checkout.payment.payment') }}</h1>
            <hr>
            <div class="columns">
                <div class="column is-two-thirds">
                    <div class="logistic-information field-container">
                        <div>
                            <div class="logistic-title hyper5 bold black">{{ $t('web.travel-sim-checkout.payment.logistic-information') }}</div>
                            <div class="information-column">
                                <div class="gray500">{{ $t('web.travel-sim-checkout.recipient-name') }}: </div>
                                <div class="bold">{{ form.recipientName }}</div>
                            </div>
                            <div class="information-column">
                                <div class="gray500">{{ $t('web.travel-sim-checkout.recipient-phone-number') }}: </div>
                                <div class="bold"> ({{form.recipientTelAreaCode}}) {{form.recipientTel}}</div>
                            </div>
                            <div class="information-column">
                                <div class="gray500">{{ $t('web.travel-sim-checkout.user-email') }}: </div>
                                <div class="bold">{{form.email}}</div>
                            </div>
                            <div class="information-column" v-if="sim.type == 'sim'">
                                <div class="gray500">{{ $t('web.travel-sim-checkout.recipient-address') }}: </div>
                                <div class="bold">{{form.address1}}<br>{{form.address2}}<br>{{form.address3}}</div>
                            </div>
                            <div class="information-column" v-if="sim.type == 'sim'">
                                <div class="gray500">{{ $t('web.travel-sim-checkout.delivery-method') }}: </div>
                                <div class="bold">{{ $t('product-simcard.shipping-methods.' + form.shippingMethod) }}</div>
                            </div>
                        </div>
                        <a @click.prevent="currentStep = 1" class="brand-blue bold modify-info">{{ $t('web.travel-sim-checkout.payment.modify-information') }}</a>
                    </div>

                    <div class="sim-detail field-container">
                        <div class="title hyper5 bold black">{{ $t('web.travel-sim-checkout.simproduct-detail') }}</div>
                        <div class="border">
                            <div class="simcard-product">{{ $t('web.travel-sim-checkout.simproduct-title') }}</div>
                        </div>
                        <div class="sim-container border">
                            <div class="word-container">
                                <div class="word-container-1">
                                    <div class="hyper6 bold">{{sim.name}}</div>
                                    <div><span class="bold" style="color:#0176EE">{{currency_prefix}}{{foreign_price_app.toLocaleString()}}</span> x{{form.quantity}}</div>
                                </div>
                            </div>
                            <img :src="sim.image_url" class="image-container">
                        </div>
                        <div class="sub-total" v-if="form.shippingMethod == 'international'">
                            <div class="sim-price border">
                                <div class="sub-total-title bold">{{ $t('web.travel-sim-checkout.payment.subtotal') }}</div>
                                <div class="hyper6 brand-blue bold">{{currency_prefix}} {{(foreign_price_app * form.quantity).toLocaleString()}}</div>
                            </div>
                            <div class="shipping-price border">
                                <div class="bold">{{ $t('web.travel-sim-checkout.payment.shipping-fee') }}</div>
                                <div class="body bold">{{currency_prefix}}{{(30/currency_selling_rate).toLocaleString(undefined,{maximumFractionDigits: 2})}}</div>
                            </div>

                        </div>
                        <div class="check-out-price border" v-if="form.shippingMethod == 'international'">
                            <div class="foreign-price-container">
                                <div class="sim-product bold">{{ $t('web.travel-sim-checkout.payment.price') }}</div>
                                <div class="hyper6 brand-blue bold">{{currency_prefix}} {{ ((foreign_price_app * form.quantity) + (30/currency_selling_rate)).toLocaleString(undefined,{maximumFractionDigits: 2}) }}</div>
                            </div>
                            <div v-if="currency_selling_rate != 1" class="payment small">{{ $t('web.travel-sim-checkout.payment.actual-payment',{actual_payment: sim.price_app * form.quantity + 30})}}</div>
                        </div>
                        <div class="check-out-price border" v-if="form.shippingMethod != 'international'">
                            <div class="foreign-price-container">
                                <div class="sim-product bold">{{ $t('web.travel-sim-checkout.payment.price') }}</div>
                                <div class="hyper6 brand-blue bold">{{currency_prefix}}{{(foreign_price_app * form.quantity).toLocaleString()}}</div>
                            </div>
                            <div v-if="currency_selling_rate != 1" class="payment small">{{ $t('web.travel-sim-checkout.payment.actual-payment',{actual_payment: sim.price_app * form.quantity})}}</div>
                        </div>
                    </div>

                    <div class="field-container stripe-checkout">
                        <div class="title hyper5 bold black">{{ $t('web.travel-sim-checkout.payment-method') }}</div>
                        <div class="online-payment-security body bold black"><i class="fas fa-lock"></i>{{ $t('web.travel-sim-checkout.secure-payment-slogan') }}</div>

                        <div class="control">
                            <p style="margin-bottom: 8px">{{ $t('web.travel-sim-checkout.payment.choose-payment-method') }}</p>
                            <label class="radio payment-options-button">
                                <input type="radio" name="payment_method" value="payme" v-model="paymentMethod">
                                <img src="/images/payme.svg" />
                            </label>

                            <label class="radio payment-options-button">
                                <input type="radio" name="payment_method" value="stripe" v-model="paymentMethod">
                                <img src="/images/credit-cards.svg" />
                            </label>
                        </div>

                        <div class="payment-container">
                            <div class="stripe-container" v-if="paymentMethod === 'stripe'">
                                <card class='stripe-card'
                                      :stripe='stripe.instance'
                                      :options='stripe.options'
                                      @change='stripe.complete = $event.complete'
                                />
                                <div class="card-images">
                                    <div class="card-image">

                                    </div>
                                    <div class="powered-by-stripe">
                                        <img src="/images/powered-by-stripe.svg" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="confirmation">
                        <button v-bind:class="{ 'is-loading': isLoading, button: isLoading}" @click.prevent="pay" class="confirm-button hyper6 bold white" :disabled="isLoading">{{ $t('web.travel-sim-checkout.payment.pay-now') }}</button>
                        <button @click.prevent="back" class="back-button hyper6 bold black">{{ $t('web.travel-sim-checkout.back-button') }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { Card, createToken } from 'vue-stripe-elements-plus'
    import axios from 'axios'
    export default {
        components: { Card },

        props: {
            user: Object,
            sim: Object,
            foreign_price_app: Number,
            currency_prefix: String,
            currency_selling_rate: Number,
            countries: Array,
            routes: Object,
            stripeKey: String,
            errorMessage: String,
            h: String,
            apiDomain: String,
        },

        data: function(){
            return {
                isLoading: false,
                token: document.head.querySelector('meta[name="csrf-token"]').content,
                currentStep: 1,
                loginStep1: 1,
                paymentMethod: 'stripe',
                paymeRes: null,
                form: {
                    quantity: 1,
                    firstname: "",
                    lastname: "",
                    email: "",
                    tel: "",
                    telAreaCode: "852",
                    recipientName: "",
                    recipientTel: "",
                    recipientTelAreaCode: "852",
                    address1: "",
                    address2: "",
                    address3: "",
                    shippingMethod: "hkpost",
                    cardId: null,
                    source: "credit-card"
                },
                stripe: {
                    instance: null,  // To be init in mount
                    complete: false,
                    options: {
                        style: {
                            base: {
                                color: '#32325d',
                                fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                                fontSmoothing: 'antialiased',
                                fontSize: '16px',
                                '::placeholder': {
                                    color: '#aab7c4'
                                }
                            },
                            invalid: {
                                color: '#fa755a',
                                iconColor: '#fa755a'
                            }
                        }
                    }
                }
            }
        },
        computed: {
            sortedPhoneCodes() {
                let sortedPhoneCodes = this.countries.sort((a, b) => {
                    return a.phone_code - b.phone_code;
                }).map(country=>{
                    return country.phone_code;
                });
                var filteredPhoneCodes = new Set(sortedPhoneCodes);
                return filteredPhoneCodes;
            },
        },
        mounted: function () {
            if (this.user) {
                this.form.firstname = this.user.firstname;
                this.form.lastname = this.user.lastname;
                this.form.email = this.user.email;
                this.form.tel = this.user.tel;
                this.form.recipientName = this.user.recipient_name;
                this.form.recipientTel = this.user.recipient_tel;
                this.form.address1 = this.user.address1;
                this.form.address2 = this.user.address2;
                this.form.address3 = this.user.address3;
            }

            let stripe = Stripe(this.stripeKey, {
                locale: 'en'
            });
            this.stripe.instance = stripe;
            this.$ga.event('checkout', 'init');
            this.$ga.ecommerce.addItem({
                "id": Math.random().toString(36).substring(2) + "" + Math.random().toString(36).substring(2),
                "name": this.sim.name,
                "list_position": 1,
                "quantity": this.form.quantity,
                "price": this.sim.price_app
            });
            this.$ga.ecommerce.send();
        },
        methods: {
            confirmStep1(){
                if(this.form.firstname == "" || !this.form.firstname ||this.form.lastname == "" || !this.form.lastname ||this.form.email == "" || !this.form.email||this.form.tel == "" || !this.form.tel){
                    return alert( this.$t('web.travel-sim-checkout.error-alert-personal-info') );
                }else if(this.form.telAreaCode == "852"&& (!this.form.tel || this.form.tel.length != 8)) {
                    return alert(this.$t('web.travel-sim-checkout.error-alert-invalid-user-number'));
                }else if(!this.form.email.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/)){
                    return alert( this.$t('web.travel-sim-checkout.error-alert-invalid-email'));
                }
                if(this.sim.type == 'sim') {
                    if (this.form.recipientName == "" || !this.form.recipientName || this.form.recipientTel == "" || !this.form.recipientTel) {
                        return alert(this.$t('web.travel-sim-checkout.error-alert-recipient-info'));
                    } else if ((this.form.address1 == "" || !this.form.address1) &&
                        (this.form.address2 == "" || !this.form.address2) &&
                        (this.form.address3 == "" || !this.form.address3)) {
                        return alert(this.$t('web.travel-sim-checkout.error-alert-address'));
                    } else if (this.form.recipientTelAreaCode == "852" && (!this.form.recipientTel || this.form.recipientTel.length != 8)) {
                        return alert(this.$t('web.travel-sim-checkout.error-alert-invalid-recipient-number'));
                    } else if (this.form.telAreaCode == "852" && (this.form.tel && this.form.tel.length == 8 && parseInt(this.form.tel[0]) < 5)) {
                        return alert(this.$t('web.travel-sim-checkout.error-alert-invalid-user-first-num'));
                    } else if (this.form.recipientTelAreaCode == "852"
                        && (this.form.recipientTel && this.form.recipientTel.length == 8
                            && (parseInt(this.form.recipientTel[0]) == 1
                                || parseInt(this.form.recipientTel[0]) == 4
                                || parseInt(this.form.recipientTel[0]) == 0))) {
                        return alert(this.$t('web.travel-sim-checkout.error-alert-invalid-recipient-first-num'));
                    }
                }
                this.$ga.event('checkout', 'confirm');
                $("html, body").animate({ scrollTop: 0 }, 500);
                setTimeout(() => {
                    this.currentStep = 2;
                }, 250);
            },

            back(){
                $("html, body").animate({ scrollTop: 0 }, 500);
                setTimeout(() => {
                    this.currentStep = 1;
                }, 250);
            },

            loginStep(){
                if(this.loginStep1 == 1){
                    this.loginStep1 = 2;
                }else if(this.loginStep1 == 2){
                    this.loginStep1 = 1;
                }
            },

            autoFillTel(){
                if(!this.form.recipientTel){
                    this.form.recipientTel = this.form.tel;
                }
            },

            autoFillName(){
                if(this.form.firstname && this.form.lastname && !this.form.recipientName){
                    this.form.recipientName = this.form.firstname + " " + this.form.lastname;
                }
            },

            autoFillTelAreaCode(){
                this.form.recipientTelAreaCode = this.form.telAreaCode;
            },

            goBack(){
                window.location = this.routes['travel_sim']
            },

            async pay(){
                this.isLoading = true;
                let cardId, source;
                switch (this.paymentMethod) {
                    case "payme":
                        source = 'payme';
                        cardId = 'payme';
                        break;
                    case "stripe":
                        let data;
                        try {
                            data = await createToken();
                        }catch(e) {
                            this.isLoading = false;
                            return alert(this.$t('web.travel-sim-checkout.error-invalid-credit-card-info'));
                        }
                        if(!data || !data.token){
                            this.isLoading = false;
                            return alert(this.$t('web.travel-sim-checkout.error-invalid-credit-card-info'));
                        }
                        cardId = data.token.id;
                        source = 'credit-card';
                        break;
                }
                try{
                    let res = await axios.post(window.location.href, {
                        "promotionCodeId": null,
                        'quantity': this.form.quantity,
                        'firstname': this.form.firstname,
                        'lastname': this.form.lastname,
                        'email': this.form.email,
                        'telAreaCode': this.form.telAreaCode,
                        'tel': this.form.tel,
                        'recipientName': this.form.recipientName,
                        'recipientTel': this.form.recipientTel,
                        'recipientTelAreaCode': this.form.recipientTelAreaCode,
                        'address1': this.form.address1,
                        'address2': this.form.address2,
                        'address3': this.form.address3,
                        'shippingMethod': this.form.shippingMethod,
                        'cardId': cardId,
                        'source': source,
                        'h': this.h
                    });
                    if(res && res.data && res.data.success){
                        this.successCallback(res.data.order_number);
                    }else{
                        this.isLoading = false;
                        alert(res.data.message);
                    }
                }catch(e){
                    this.isLoading = false;
                    if(e.response && e.response.data && e.response.data.message){
                        alert(e.response.data.message);
                    }else{
                        alert(e);
                    }
                }
            },

            successCallback(orderNumber){
                switch (this.paymentMethod) {
                    case "payme":
                        window.location = window.location.origin + window.location.pathname.replace(/\/+$/, '') + "/pending?orderNumber=" + orderNumber + "&productId=" + this.sim.id + "&email=" + encodeURI(this.form.email) + `&orderValue=${this.sim.price_app * this.form.quantity}&orderCurrency=HKD`;
                        break;
                    case "stripe":
                        this.$ga.ecommerce.addTransaction({
                            "id": orderNumber,
                            "affiliation": "HyperAir Website",
                            "value": this.sim.price_app * this.form.quantity,
                            "currency": "HKD",
                            "tax": 0,
                            "shipping": 0,
                            "items": [
                                {
                                    "id": this.sim.id,
                                    "name": this.sim.name,
                                    "list_position": 1,
                                    "quantity": this.form.quantity,
                                    "price": this.sim.price_app
                                }
                            ]
                        });
                        this.$ga.ecommerce.send();
                        window.location = window.location.origin + window.location.pathname + "/success?orderNumber=" + orderNumber + "&productId=" + this.sim.id + "&email=" + encodeURI(this.form.email) + `&orderValue=${this.sim.price_app * this.form.quantity}&orderCurrency=HKD`;
                        break;
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../sass/_mixin.scss";
    hr{
        background-color: #DEDEDE;
    }
    .field-container{
        padding: 16px;
    }
    .title{
        margin-bottom: 4%;
    }
    .label{
        font-weight: initial;
    }

    .container-1{
        .sim-checkout-title{
            margin-top: 16px;
            @include responsive(){
                font-size: 2.5rem;
            }
        }
        .country-code{
            margin-right: 2rem;
            .select:not(.is-multiple):not(.is-loading)::after{
                border-color: #0176EE;
                border-width: 3px;
                border-radius: 2px;
            }
        }
        .sim-detail{
            background: white;
            margin-bottom: 4%;
            @include responsive(){
                padding: 8px;
            }
            .border{
                border: 1px solid #DEDEDE;
                width: 100%;
            }
            .simcard-product{
                padding: 1% 2%;
            }
            .sim-container{
                border-top: none;
                display: flex;
                justify-content: space-between;
                padding: 2%;
                @include responsive(){
                    flex-direction: column-reverse;
                }
                .word-container{
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    @include responsive(){
                        padding: 8px;
                    }
                    .select:not(.is-multiple):not(.is-loading)::after{
                        border-color: #3273dc;
                    }
                    .button{
                        align-items: baseline;
                        .fas{
                            font-weight: initial;
                            color: #0176EE
                        }
                    }
                }
                .word-container-1{
                    @include responsive(){
                        margin-top: 8px;
                    }
                }
                .word-container-2{
                    @include responsive(){
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                }
                img{
                    height:150px;
                    width: 140px;
                    object-fit: cover;
                    object-position: center;
                    @include responsive(){
                        width: 100%;
                        height: 240px;
                    }
                }
            }
            .check-out-price{
                display: flex;
                justify-content: space-between;
                padding: 2%;
                border-top: none;
            }
        }
        .simcheckout-login{
            background: white;
            .login-column{
                display: flex;
                justify-content: space-between;
                align-items: center;
                &:hover{
                    color: #0170e1;
                    .login{
                        text-decoration: underline;
                    }

                }
                .fas{
                    font-size: 2rem;
                    color: #52616B;
                }
            }
        }
        .fade-enter-active, .fade-leave-active {
            transition: opacity .8s;
        }
        .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
            opacity: 0;
        }
        .hidden-part{
            background: white;
            margin-bottom: 4%;
            display:flex;
            justify-content: space-between;
            align-items: center;
            .columns{
                width: 100%;
                margin-top: 0;

            }
            a,
            button{
                width: 162px;
                height: 48px;
                background: #0176EE;
                border-radius: 5px;
                border:none;
                cursor: pointer;
                @include responsive(){
                    width: 100%;
                }
            }
            .image-container{
                width: 265px;
                padding: 8% 0%;
                @include responsive(){
                    width: 100%;
                }
                .facebook-login{
                    position: relative;
                    background: #3B5999;
                    border-radius: 5px;
                    margin-bottom: 12px;
                    border-color: #3B5999;
                    display: block;
                    width: 265px;
                    height: 48px;
                    align-items: center;
                    padding: 11px 0;
                    text-align: center;
                    @include responsive(){
                        width: 100%;
                    }
                    &:lang(en-US){
                        padding-left: 12px;
                    }
                    .facebook{
                        position: absolute;
                        left: 0;
                        margin-left: 25px;
                        padding: 3px 0;
                    }
                    .facebook:lang(en-US){
                        margin-left: 12px;
                    }
                    &:hover{
                        background: #4e69a3;
                        color: white;
                    }
                }
                .google-login{
                    position: relative;
                    background: #EEEEEE;
                    border-radius: 5px;
                    border-color: #EEEEEE;
                    display: block;
                    width: 265px;
                    height: 48px;
                    align-items: center;
                    padding: 11px 0;
                    text-align: center;
                    @include responsive(){
                        width: 100%;
                    }
                    .google{
                        position: absolute;
                        left: 0;
                        margin-left: 25px;
                        padding: 3px 0;
                        &:lang(en-US){
                            margin-left: 12px;
                        }

                    }
                    &:hover{
                        background: #F7F7F7;
                    }

                }
            }
        }
        .logged-in{
            background: white;
            margin-bottom: 4%;
            padding: 8px 16px;

        }
        .user-information{
            background: white;
            margin-top: 4%;
            margin-bottom:4%;
            .is-horizontal{
                @include responsive(){
                    display: flex;
                }
            }
        }
        .recipient-information{
            background:white;
            margin-bottom:4%;
            .is-horizontal{
                @include responsive(){
                    display: flex;
                }
            }
            .recipient-tel{
                &:lang(en-US){
                    width: 32%;
                    @include responsive(){
                        width: 60%;
                    }
                }
            }
        }
        .address{
            background: white;
            margin-bottom:4%;
            .input{
                margin-bottom: 16px;
            }
        }
        .delivery-method{
            background: white;
            margin-bottom:4%;
            .checkbox input, .radio input{
                margin-right: 0.5rem;
            }
            p{
                margin-top: 6px;
                margin-left: 23px;
            }
            .hk-post, .sf{
                margin-bottom: 16px;
            }
            .message{
                margin-top: 16px;
                .message-header{
                    padding: 8px 8px;
                }
                .has-text-info{
                    color: white !important;
                }
                .message-body{
                    padding: 8px 11px;
                }
            }
        }
        .confirmation{
            align-items: center;
            margin-bottom: 4%;
            .confirm-button{
                background: #0176EE;
                border-radius: 5px;
                cursor: pointer;
                border: none;
                width: 276px;
                height: 48px;
                margin-right: 4%;
                @include responsive(){
                    width: 100%;
                }
            }
            .back-button{
                cursor: pointer;
                background: #FFFFFF;
                border-radius: 5px;
                border: 1px solid #DEDEDE;
                width: 90px;
                height: 48px;
                @include responsive(){
                    display: none;
                }
            }
        }
    }

    .container-2{
        .sim-checkout-title{
            @include responsive(){
                font-size: 2.5rem;
            }
        }
        .logistic-information{
            background: white;
            margin-bottom: 4%;
            display: flex;
            justify-content: space-between;
            .logistic-title{
                margin-bottom: 8px;
            }
            .information-column{
                margin-bottom: 16px;
            }
            .modify-info{
                height: 21px;
                &:hover{
                    color: #0170e1;
                    text-decoration: underline;
                }
            }
        }

        .sim-detail{
            background: white;
            margin-bottom: 4%;
            .border{
                border: 1px solid #DEDEDE;
                width: 100%;
            }
            .simcard-product{
                padding: 1% 2%;
            }
            .sim-container{
                border-top: none;
                border-bottom: none;
                display: flex;
                justify-content: space-between;
                padding: 2%;
                @include responsive(){
                    flex-direction: column-reverse;
                }

                img{
                    height:150px;
                    width: 140px;
                    object-fit: cover;
                    object-position: center;
                    @include responsive(){
                        width: 100%;
                        height: 240px;
                    }
                }
            }
            .sub-total{
                .sim-price{
                    padding: 2%;
                    border-bottom: none;
                    display: flex;
                    justify-content: space-between;
                }
                .shipping-price{
                    padding: 2%;
                    border-bottom: none;
                    display: flex;
                    justify-content: space-between;
                }
            }
            .check-out-price{
                padding: 2%;
                .foreign-price-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .payment{
                    width: 55%;
                    margin-left: auto;
                    text-align: end;
                    @include responsive(){
                        width: 100%;
                    }
                }
            }
        }
        .confirmation{
            align-items: center;
            margin-bottom: 4%;
            @include responsive(){
                margin-bottom: 8%;
            }
            .confirm-button{
                cursor: pointer;
                background: #0176EE;
                border-radius: 5px;
                border: none;
                width: 276px;
                height: 48px;
                margin-right: 4%;

                &[disabled]{
                    //ToDo
                }

                @include responsive(){
                    width: 100%;
                }
            }
            .back-button{
                cursor: pointer;
                background: #FFFFFF;
                border-radius: 5px;
                border: 1px solid #DEDEDE;
                width: 90px;
                height: 48px;

                @include responsive(){
                    display: none;
                }
            }
            .red{
                background: red;
            }

        }

        .stripe-checkout{
            margin-bottom: 4%;
            background: #FFF;

            .online-payment-security{
                background: #EEEEEE;
                border: 1px solid #DEDEDE;
                box-sizing: border-box;
                border-radius: 4px;
                padding: 8px;
                margin-bottom: 16px;
                .fas{
                    color: #999999;
                    margin-right: 12px;
                }
            }

            .payment-options-button{
                margin-right: 32px;

                input{
                    margin-right: 8px;
                }

                img{
                    border: 1px solid #EEEEEE;
                    display: inline-block;
                    vertical-align: middle;
                    padding: 8px 20px;
                    border-radius: 5px;
                }

                @include responsive(){
                    & + .payment-options-button{
                        margin-left: 0;
                        margin-top: 8px;
                    }
                }
            }

            .payment-container{
                margin-top: 14px;
            }

            .stripe-card{
                border: 1px solid #DEDEDE;
                margin-bottom: 12px;
                padding: 8px;
            }

            .card-images{
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
        }

    }

</style>
